import { Door, DoorType, Location } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { getOfficeModeTime } from 'utils/Lock/Lock';
import { EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';

/**
 * The doors query fetches only `locationIds` (no nested `locations` resolver),
 * so stitch the location entities back in from the locations already held in the cache.
 */
export const stitchDoorLocations = (doors: Door[], locations: Location[]): Door[] => {
  const locationsById = new Map(locations.map((location) => [location.id, location]));

  return doors.map((door) => ({
    ...door,
    locations: door.locationIds
      .map((locationId) => locationsById.get(locationId))
      .filter((location): location is Location => Boolean(location))
  }));
};

/**
 * The slim locations (device groups) query only selects the id of each door (device), so resolve them to the doors
 * fetched by a doors query. A door that isn't in `doorsById` is kept as the id-only door, so the counts based on
 * `doors.length` don't change.
 */
export const resolveLocationDoors = (location: Location, doorsById: Map<string, Door>): Door[] =>
  location.doors.map((door) => doorsById.get(door.id) ?? door);

export const isOfficeModeAvailable = (door: Door) => door.type !== DoorType.Tanlock;

export const isOfficeModeEnabled = (door: Door) => door.officeMode.weekDays.length > 0;

export const isOfficeModeAvailableAndEnabled = (door: Door) => isOfficeModeAvailable(door) && isOfficeModeEnabled(door);

export const getOfficeModeDisplayText = (door: Door | undefined, translation: Translation) => {
  if (!door || !isOfficeModeAvailable(door)) return translation.not_available;
  return isOfficeModeAvailableAndEnabled(door) ? getOfficeModeTime(door) : EMPTY_VALUE_PLACEHOLDER;
};

export const getLockTypeName = (type: DoorType | undefined, translation: Translation): string => {
  switch (type) {
    case DoorType.CloudControllerDoor:
      return translation.cloud_controller_door;
    case DoorType.DomCylinder:
      return translation.cylinder;
    case DoorType.DomHandle:
      return translation.handle;
    case DoorType.DomReader:
      return translation.reader;
    case DoorType.DomLocker:
      return translation.locker;
    case DoorType.DomController:
      return translation.controller;
    case DoorType.Tanlock:
      return translation.swing_handle;
    default:
      return translation.unknown;
  }
};
